import { useEffect, useState } from "react";
import { useTheme } from "../ThemeProvider";
import api from "../../services/api";

function fmtFecha(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtHora(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
}

function SeccionLista({ titulo, icono, items, vacio, renderItem, theme }) {
  const t = theme.colors;
  return (
    <div className="mb-4">
      <h3 className="text-sm font-bold mb-2 flex items-center gap-2" style={{ color: t.textPrimary }}>
        <span>{icono}</span> {titulo}
        <span className="text-xs font-normal" style={{ color: t.textMuted }}>
          ({items.length})
        </span>
      </h3>
      {items.length === 0 ? (
        <p className="text-xs px-2 py-3 rounded-lg" style={{ color: t.textMuted, background: t.bgInput }}>
          {vacio}
        </p>
      ) : (
        <div className="flex flex-col gap-1.5 max-h-48 overflow-y-auto">
          {items.map(renderItem)}
        </div>
      )}
    </div>
  );
}

export default function ModalActividadPropiedad({ open, onClose, propiedad }) {
  const { theme } = useTheme();
  const t = theme.colors;
  const [cargando, setCargando] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !propiedad?.id) return;
    setCargando(true);
    setError("");
    setDetalle(null);
    api
      .get(`/propiedades/${propiedad.id}/actividad`)
      .then((res) => setDetalle(res.data?.data || null))
      .catch((e) => setError(e?.response?.data?.error || "No se pudo cargar el detalle"))
      .finally(() => setCargando(false));
  }, [open, propiedad?.id]);

  if (!open) return null;

  const itemSt = {
    padding: "8px 10px",
    borderRadius: 8,
    background: t.bgInput,
    border: `1px solid ${t.border}`,
    fontSize: 12,
    color: t.textPrimary,
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.55)" }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl shadow-2xl p-5"
        style={{ background: t.bgCard, border: `1px solid ${t.border}` }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg font-bold" style={{ color: t.textPrimary }}>
              Actividad · Ref {propiedad?.referencia || "—"}
            </h2>
            <p className="text-xs mt-1" style={{ color: t.textMuted }}>
              {propiedad?.titulo || "Sin título"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-xl leading-none"
            style={{ color: t.textMuted, background: "none", border: "none", cursor: "pointer" }}
          >
            ✕
          </button>
        </div>

        {cargando && <p style={{ color: t.textMuted }}>Cargando…</p>}
        {error && <p style={{ color: "#ef4444" }}>{error}</p>}

        {detalle && !cargando && (
          <>
            <SeccionLista
              theme={theme}
              titulo="Visitas"
              icono="🏠"
              items={detalle.visitas || []}
              vacio="Sin visitas registradas en agenda"
              renderItem={(v) => (
                <div key={v.id} style={itemSt}>
                  <div className="font-semibold">{v.titulo || "Visita"}</div>
                  <div style={{ color: t.textMuted, marginTop: 2 }}>
                    {fmtFecha(v.fecha_inicio)}
                    {v.fecha_inicio ? ` · ${fmtHora(v.fecha_inicio)}` : ""}
                    {v.estado ? ` · ${v.estado}` : ""}
                  </div>
                  {(v.lead_nombre?.trim() || v.cliente_nombre?.trim() || v.agente_nombre) && (
                    <div style={{ color: t.textMuted, marginTop: 2, fontSize: 11 }}>
                      {[v.lead_nombre?.trim() || v.cliente_nombre?.trim(), v.agente_nombre && `Agente: ${v.agente_nombre}`]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  )}
                </div>
              )}
            />

            <SeccionLista
              theme={theme}
              titulo="Leads por referencia"
              icono="📥"
              items={detalle.leads || []}
              vacio="Sin leads con esta referencia"
              renderItem={(l) => (
                <div key={l.id} style={itemSt}>
                  <div className="font-semibold">
                    {[l.nombre, l.apellido].filter(Boolean).join(" ") || "Sin nombre"}
                  </div>
                  <div style={{ color: t.textMuted, marginTop: 2 }}>
                    {fmtFecha(l.created_at)} · {l.fuente || "—"} · {l.etapa || "—"}
                  </div>
                  {(l.telefono || l.email) && (
                    <div style={{ color: t.textMuted, marginTop: 2, fontSize: 11 }}>
                      {[l.telefono, l.email].filter(Boolean).join(" · ")}
                    </div>
                  )}
                </div>
              )}
            />

            <SeccionLista
              theme={theme}
              titulo="Ofertas"
              icono="💰"
              items={detalle.ofertas || []}
              vacio="Sin ofertas registradas en historial"
              renderItem={(o) => (
                <div key={o.id} style={itemSt}>
                  <div className="font-semibold">{o.tipo === "nota" ? "Nota (oferta)" : o.tipo}</div>
                  <div style={{ color: t.textMuted, marginTop: 2 }}>
                    {fmtFecha(o.created_at)}
                    {o.canal ? ` · ${o.canal}` : ""}
                  </div>
                  {o.contacto_nombre?.trim() && (
                    <div style={{ color: t.textMuted, marginTop: 2, fontSize: 11 }}>
                      {o.contacto_nombre.trim()}
                    </div>
                  )}
                  {o.contenido && (
                    <div style={{ marginTop: 4, fontSize: 11, whiteSpace: "pre-wrap" }}>{o.contenido}</div>
                  )}
                </div>
              )}
            />
          </>
        )}
      </div>
    </div>
  );
}
